import { sha256 } from "sha.js";

const hash = s => {
	return new sha256().update(s, "utf8").digest("hex");
};

import TrackingState from "./TrackingState";
import GdprStorage from "../storage/GdprStorage";
import { PrefixedStorage } from "../PrefixedStorage";
import SessionState from "./SessionState";
import { FeatureActions } from "../actions/FeatureActions";
import { Store } from "../utils/Store";
import { initFeatures } from "./FeatureRegistration";
import CountryUtil from "../utils/CountryUtil";
import type { StorageFrontend } from "../storage/GdprStorage";
type Group = "A" | "B";
type Feature = {
	description?: string;
	descriptionA?: string;
	descriptionB?: string;
	id?: string;
	country?: string;
	active?: boolean;
	ratioAB?: number;
	group?: Group;
	updateTimestamp?: number;
};
type FeatureToggle = {
	id: string;
	country: string;
	group: Group;
};
type ActiveABTest = {
	id: string;
	country: string;
	active: boolean;
	ratioAB: number;
	updateTimestamp: number;
};
const updateABCookieDateName = "updateABCookieDate";
const abGroupsCookieName = "abGroups";
const ABtestCountries = [
	"HU",
	"KZ",
	"RU",
	"UA",
	"PK",
	"ES",
	"IT",
	"PL",
	"RO",
	"NL",
	"FR",
	"DE",
	"BG",
	"MD",
	"PT",
	"RS",
];
export const Features: Record<string, Feature> = initFeatures();

class InternalStore extends Store {
	memberships: Record<string, Group>;
	storage: StorageFrontend;
	features: Record<string, Feature>;
	featureToggles: Record<string, FeatureToggle>;
	configuredFeatures: Record<string, ActiveABTest>;
	lastupdateABCookieTimestamp: number;
	currentABCookie: Record<string, Group>;

	constructor(features: Record<string, Feature>) {
		super();
		this.features = features;
		this.configuredFeatures = window.abExperiments || {};
		this.featureToggles = window.abExperimentsFeatureToggles || {};
		this.listenTo(FeatureActions.setMembership, this.setMembershipFor);
		this.listenTo(FeatureActions.recalculateMemberships, this.recalculateGroupMemberships);
		this.memberships = {};
		this.storage = PrefixedStorage("FEAT_", GdprStorage.localStorage);
		const currentABgroupString = GdprStorage.cookie.read(abGroupsCookieName);
		const lastupdateABCookieTimestampString = GdprStorage.localStorage.read(updateABCookieDateName);
		this.currentABCookie = {};

		if (currentABgroupString) {
			try {
				this.currentABCookie = JSON.parse(currentABgroupString);
			} catch {
				// Moving on, using the default
			}
		}

		this.lastupdateABCookieTimestamp = lastupdateABCookieTimestampString
			? Number(lastupdateABCookieTimestampString)
			: 0;
		this.updateFeatures();
	}

	setTestGroups() {
		const abTestGroups = this.getActiveGroupMemberships();
		const abTrackingGroups = Object.fromEntries(
			Object.entries(abTestGroups).map(([key, value]) => [`abGroups_${key}`, value]),
		);
		TrackingState.setTestGroups(abTrackingGroups);
	}

	getUserId: (...args: Array<any>) => any = () => {
		const sessionState = SessionState.getState();
		return (
			sessionState &&
			((sessionState.employeeData && sessionState.employeeData.upn) ||
				sessionState.customerId ||
				sessionState.anonymousUserId)
		);
	};
	recalculateGroupMemberships: (...args: Array<any>) => any = (suppressTrigger?: boolean) => {
		if (this.getUserId() !== undefined) {
			const memberships = {};
			let changed = false;

			for (const k in this.features) {
				memberships[k] = this.calculateMembershipFor(k);

				if (memberships[k] !== this.memberships[k] || k in memberships !== k in this.memberships) {
					changed = true;
				}
			}

			if (changed) {
				this.memberships = memberships;
				this.setTestGroups();
				this.setCookie();

				if (!suppressTrigger) {
					this.trigger({ ...this.memberships });
				}
			}
		}
	};

	getGroupMemberships(): Record<string, Group> {
		return { ...this.memberships };
	}

	getActiveGroupMemberships(): Record<string, Group> {
		const activeGroups = Object.keys(this.features).filter(key => this.isAbTestingActive(key));
		return Object.fromEntries(activeGroups.map(key => [key, this.memberships[key]]));
	}

	isAbTestingActive(group: string): boolean {
		if (!this.features[group]) {
			return false;
		}

		return this.features[group].active ? this.features[group].active! : false;
	}

	calculateMembershipFor(group: string): Group | void {
		if (!this.features[group]) {
			return undefined;
		}

		const manuallySetMembership = this.storage.read(group);

		if (manuallySetMembership) {
			return manuallySetMembership as any;
		}

		const experimentTimestamp = this.features[group].updateTimestamp;
		const currentABgroup = this.currentABCookie[group];

		if (experimentTimestamp && currentABgroup && this.lastupdateABCookieTimestamp > experimentTimestamp) {
			return currentABgroup;
		}

		const defaultGroup = this.features[group].group!;

		if (!this.isAbTestingActive(group)) {
			if (["A", "B"].includes(defaultGroup)) {
				return defaultGroup;
			} else {
				return undefined;
			}
		} else {
			const ratioAB = this.features[group].ratioAB;

			if (ratioAB || ratioAB === 0) {
				const userId = this.getUserId();
				const membership = parseInt(hash(userId + group).slice(-2), 16) % 128;
				return membership / 128 < ratioAB / 100 ? "A" : "B";
			} else {
				return defaultGroup;
			}
		}
	}

	getMembershipFor(experiment: string): Group | void {
		if (this.memberships[experiment]) {
			return this.memberships[experiment];
		}

		const membership = this.calculateMembershipFor(experiment);

		if (membership) {
			return membership;
		}
	}

	setMembershipFor: (...args: Array<any>) => any = (group: string, membership: Group) => {
		if (!["A", "B"].includes(membership)) {
			return;
		}

		if (!this.features[group]) {
			return;
		}

		this.storage.put(group, membership, GdprStorage.NECESSARY);
		this.memberships[group] = membership;
		this.trigger({ ...this.memberships });
	};

	updateFeatures() {
		for (const key in this.configuredFeatures) {
			this.features[key] = { ...this.features[key], ...this.configuredFeatures[key] };
		}

		for (const key in this.featureToggles) {
			this.features[key] = { ...this.features[key], ...this.featureToggles[key] };
		}

		this.recalculateGroupMemberships();
	}

	setCookie() {
		const activeGroups = this.getActiveGroupMemberships();
		let abGroupsString = "{}";

		try {
			abGroupsString = JSON.stringify(activeGroups) || "{}";
		} catch {
			// Moving on, using the default
		}

		const updateABCookieDate = new Date();
		const updateABCookieTimestamp = Math.floor(updateABCookieDate.getTime() / 1000);
		const currentCountry = CountryUtil.getCountry();
		const expirationCookieDate = ABtestCountries.includes(currentCountry)
			? new Date(updateABCookieDate.getTime() + 30 * 24 * 60 * 60 * 1000)
			: new Date(updateABCookieDate.getTime() + 1 * 24 * 60 * 60 * 1000);
		GdprStorage.cookie.put(abGroupsCookieName, abGroupsString, GdprStorage.FUNCTIONAL, {
			expires: expirationCookieDate,
		});
		// because it is not possible to extract the expiration date of cookie
		GdprStorage.localStorage.put(
			updateABCookieDateName,
			updateABCookieTimestamp.toString(),
			GdprStorage.FUNCTIONAL,
		);
		// Updating the state with current cookies
		this.currentABCookie = activeGroups;
		this.lastupdateABCookieTimestamp = updateABCookieTimestamp;
	}
}

export const initFeatureSessionStoreWithFeatures = (features: Record<string, Feature>): InternalStore => {
	return new InternalStore(features);
};
export default initFeatureSessionStoreWithFeatures(Features) as InternalStore;
export { FeatureActions };
